/* Label & Leaf — shared book offers: bundle cards + the handoff to the
   bundle page. Loaded by the storybook PDP (ll-storybook.js) and the
   colouring-book PDP (ll-coloring-book.js). The host page owns the offer
   popup shell, the single-book card, the child the customer personalised
   on the page, and the /cart/add.js post + reconcile flow; this module
   owns the bundle cards, the handoff to the bundle page, the shared cart
   poster and the line-item property contract.

   Bundle kinds (ll-books-catalogue.liquid sets them):
     stories   — N personalised story books        (story pages only)
     colouring — N personalised colouring books    (colouring pages only)
     mixed     — three chosen hardcovers + the three colouring books (Read &
                 Colour: retired from sale; kept for lines already in carts)
     library   — all six hardcovers in a fixed order for one child
                 (Their Complete Collection; story pages only)

   Contract storybook-bundle-v2 (one line item, the bundle product's
   variant). Every personalised item carries its own child fields; an item
   that reuses another item's photo names it in "photo ref" instead of
   uploading the file twice:
     Bundle                      resolved card title
     _Bundle contract            storybook-bundle-v2 | storybook-bundle-v3
     _Bundle ID                  two-stories | two-colouring | gift-set | complete-collection
     Book N                      "<Story title> — <Child name>"          (visible)
     _Book N story ID            the selected six-title catalogue ID
     _Book N child name          raw child name
     _Book N product             single storybook product handle (region twin)
     _Book N photo               file, or
     _Book N photo ref           "Book 1" when it reuses book 1's child
     Colouring book N            "<Title> — <Child name>"                (visible)
     _Colouring N story ID       dragon_roar | rainbow_kingdom | dino_herd
     _Colouring N child name     raw child name
     _Colouring N product        colouring product handle
     _Colouring N photo          file (its own child), or
     _Colouring N photo ref      "Book N" (mixed: same child as story N) / "Colouring book 1"
   A library line is always storybook-bundle-v3: Book 1..6 in the order of
   COLLECTION_STORY_IDS, one child name in every slot, Book 1's photo and
   "Book 1" as the photo ref of books 2..6, and no colouring properties.
   plus the host's own token / consent / Personalisation
   properties. See docs/superpowers/specs/2026-09-02-books-bundles-and-colouring-pdp-design.md. */
(function () {
  'use strict';

  var CONTRACT = 'storybook-bundle-v2';
  var FLEXIBLE_CONTRACT = 'storybook-bundle-v3';
  /* Their Complete Collection: the worker accepts its six slots only in this
     order (services/storybook-production book_bundles.py). */
  var COLLECTION_ID = 'complete-collection';
  var COLLECTION_STORY_IDS = ['luna_the_shy_dragon', 'rainbow_kingdom', 'dino_herd', 'christmas_hush', 'finding_me', 'find_christmas'];
  var defaultRandom = Math.random();

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function isUS(region) { return region === 'US'; }
  function basketWord(region) { return isUS(region) ? 'cart' : 'basket'; }
  function colouringWord(region, capital) {
    var word = isUS(region) ? 'coloring' : 'colouring';
    return capital ? word.charAt(0).toUpperCase() + word.slice(1) : word;
  }
  function personaliseWord(region) { return isUS(region) ? 'personalized' : 'personalised'; }

  function possessive(name) {
    var value = String(name || '').trim();
    return value ? value + "'s" : '';
  }

  /* A bundle title may carry [NAME]; without a name yet it degrades to a
     plain title rather than showing the token or a dangling apostrophe. */
  function bundleTitle(bundle, childName) {
    var title = String(bundle.title || '');
    if (title.indexOf('[NAME]') === -1) return title;
    var owner = possessive(childName);
    if (owner) return title.replace('[NAME]', owner);
    return title.replace('[NAME] ', '').replace('[NAME]', '');
  }

  function validName(value) {
    var normalized = String(value || '').trim();
    if (!normalized || normalized.length > 12) return false;
    try {
      return /\p{Script=Latin}/u.test(normalized)
        && /^[\p{Script=Latin}\p{M} '’ʼ\-‐]+$/u.test(normalized);
    } catch (error) {
      return /^[A-Za-zÀ-ž '’ʼ\-‐]+$/.test(normalized);
    }
  }

  function storyById(catalogue, storyId) {
    var list = (catalogue && catalogue.stories) || [];
    for (var i = 0; i < list.length; i += 1) if (list[i].storyId === storyId) return list[i];
    return null;
  }

  function colouringForStory(catalogue, storyId) {
    var list = (catalogue && catalogue.colouring) || [];
    for (var i = 0; i < list.length; i += 1) if (list[i].storyId === storyId) return list[i];
    return null;
  }

  /* Slots pick from the catalogue list this bundle personalises: colouring
     books for a colouring bundle, story books otherwise. Both lists are
     keyed by storyId, so a slot always stores a storyId. */
  function slotCatalogue(bundle, catalogue) {
    return bundle.kind === 'colouring' ? ((catalogue && catalogue.colouring) || []) : ((catalogue && catalogue.stories) || []);
  }

  function slotItem(bundle, catalogue, storyId) {
    return bundle.kind === 'colouring' ? colouringForStory(catalogue, storyId) : storyById(catalogue, storyId);
  }

  function slotCount(bundle) {
    return bundle.kind === 'colouring' ? bundle.colouring : bundle.stories;
  }

  function offerId(bundle) { return typeof bundle === 'string' ? bundle : (bundle && bundle.id) || ''; }

  /* A bundle whose books are fixed: nothing to choose, nothing to reorder.
     Takes a bundle or an offer id (the journey only knows the id). */
  function fixedSlots(bundle) {
    return offerId(bundle) === COLLECTION_ID || Boolean(bundle && bundle.kind === 'library');
  }

  /* The free printable (My Wonderful World) is advertised on the pair only.
     Owner, 2026-10-15: the collection does not advertise it for now. */
  function hasPrintableBonus(bundle) {
    return offerId(bundle) === 'two-stories' && Boolean(bundle && bundle.bonusText);
  }

  /* One random choice per page session keeps offer covers and the builder
     consistent. Finding editions and Christmas Hush have preferred pairs. */
  function defaultStoryIds(bundle, catalogue, pageStoryId) {
    var list = slotCatalogue(bundle, catalogue);
    // A story this market cannot sell leaves its own slot empty (and the
    // line invalid) rather than moving the others up.
    if (bundle.kind === 'library') return COLLECTION_STORY_IDS.slice(0, slotCount(bundle)).map(function (storyId) {
      return list.some(function (entry) { return entry.storyId === storyId; }) ? storyId : null;
    });
    var order = [];
    if (pageStoryId && list.some(function (entry) { return entry.storyId === pageStoryId; })) order.push(pageStoryId);
    if (!order.length && list.length) order.push(list[0].storyId);
    var companion = { christmas_hush: 'find_christmas', finding_me: 'find_christmas', find_christmas: 'finding_me' }[order[0]];
    if (bundle.kind !== 'colouring' && companion && list.some(function (entry) { return entry.storyId === companion; })) order.push(companion);
    var remaining = list.filter(function (entry) { return order.indexOf(entry.storyId) === -1; });
    // The classic collection still defaults to its three existing stories.
    if (bundle.kind === 'mixed') remaining = remaining.filter(function (entry) { return colouringForStory(catalogue, entry.storyId); });
    if (bundle.kind !== 'colouring' && remaining.length > 1) {
      var randomIndex = Math.floor(defaultRandom * remaining.length);
      remaining.unshift(remaining.splice(randomIndex, 1)[0]);
    }
    remaining.forEach(function (entry) { order.push(entry.storyId); });
    var out = [];
    for (var i = 0; i < slotCount(bundle); i += 1) out.push(order[i] || null);
    return out;
  }

  function flexibleBundle(bundle, catalogue, slots) {
    if (bundle.kind === 'library') return true;
    return bundle.kind !== 'colouring' && slots.some(function (slot) { return !colouringForStory(catalogue, slot.storyId); });
  }

  function bundleContract(bundle, catalogue, slots) {
    return flexibleBundle(bundle, catalogue, slots) ? FLEXIBLE_CONTRACT : CONTRACT;
  }

  /* All three colouring titles remain included even when a hardcover is a
     Finding or Christmas story. Classic v2 lines retain their matching order. */
  function collectionColouring(catalogue, slot, slots) {
    return slots.every(function (entry) { return colouringForStory(catalogue, entry.storyId); })
      ? colouringForStory(catalogue, slot.storyId) : (catalogue.colouring || [])[slot.index - 1];
  }

  /* A cover tile: the transparent book render (with its own shadow) on the
     card's ivory, no box. Labels are off on the offer cards — the covers
     say what they are. */
  function tile(image, label, className, withLabel) {
    var node = el('span', 'llbo-tile' + (className ? ' ' + className : ''));
    if (image) {
      var img = el('img');
      img.src = image;
      img.alt = withLabel ? '' : label;
      /* eager, with a declared size: the tiles sit in a popup that opens
         after the page has settled, and a lazy image with no box of its
         own can stay blank there (owner's phone, 2026-09-02) */
      img.loading = 'eager';
      img.width = 360;
      img.height = 360;
      img.decoding = 'async';
      node.appendChild(img);
    }
    if (withLabel) node.appendChild(el('span', 'llbo-tile__label', label));
    return node;
  }

  /* price + confirm travel together at the foot of every card so the
     prices line up across the row whatever each card's text needs */
  function foot(priceNode, button) {
    var node = el('div', 'llsb-offer__foot');
    node.appendChild(priceNode);
    node.appendChild(button);
    return node;
  }

  /* Price, saving, then an explicitly labelled bought-separately total.
     compareAtFormatted is null when the variant carries
     no compare-at price, and the block degrades to the price alone. */
  function priceBlock(offer) {
    var wrap = el('p', 'llsb-offer__price');
    wrap.appendChild(el('span', 'llsb-offer__price-now', offer.priceFormatted));
    if (offer.compareAtFormatted) {
      if (offer.percentOff) wrap.appendChild(el('span', 'llsb-offer__save', offer.percentOff + '% off'));
      wrap.appendChild(el('span', 'llsb-offer__price-comparison', offer.compareLabel || offer.compareAtFormatted + ' bought separately'));
    }
    return wrap;
  }

  /* ================= bundle cards ================= */

  function renderBundleCards(container, options) {
    var bundles = options.bundles || [];
    var catalogue = options.catalogue || {};
    var childName = options.childName || '';
    var cards = [];
    bundles.forEach(function (bundle) {
      if (!bundle || !bundle.variantId) return;
      var card = el('article', 'llsb-offer llsb-offer--bundle');
      card.setAttribute('data-llbo-bundle', bundle.id);
      var visual = el('div', 'llsb-offer__visual');
      var items = el('div', 'llsb-offer__items llbo-items');
      items.setAttribute('data-llsb-offer-items', '');
      var storyIds = defaultStoryIds(bundle, catalogue, options.pageStoryId);
      storyIds.forEach(function (storyId) {
        var item = slotItem(bundle, catalogue, storyId);
        if (item) items.appendChild(tile(item.image, item.title, bundle.kind === 'colouring' ? 'llbo-tile--colouring' : null, false));
      });
      if (bundle.kind === 'mixed') {
        var cardSlots = storyIds.map(function (id, i) { return { storyId: id, index: i + 1 }; });
        cardSlots.forEach(function (slot) {
          var book = collectionColouring(catalogue, slot, cardSlots);
          if (book) items.appendChild(tile(book.image, book.title, 'llbo-tile--colouring', false));
        });
      }
      items.setAttribute('data-llbo-count', String(items.children.length));
      visual.appendChild(items);
      card.appendChild(visual);
      if (options.recommendedId && bundle.id === options.recommendedId) {
        card.classList.add('llsb-offer--recommended');
        card.appendChild(el('span', 'llsb-offer__tag', bundle.recommendedLabel || 'Recommended'));
      }
      card.appendChild(el('h3', 'llsb-offer__name', bundleTitle(bundle, childName)));
      if (bundle.subtitle) card.appendChild(el('p', 'llsb-offer__sub', bundle.subtitle));
      if (bundle.upgradeText) card.appendChild(el('p', 'llsb-offer__upgrade', bundle.upgradeText));
      if (hasPrintableBonus(bundle)) {
        var bonus = el('div', 'llbo-bonus');
        bonus.appendChild(el('strong', '', bundle.bonusText));
        card.appendChild(bonus);
      }
      var choose = el('button', 'llp-btn llsb-offer__confirm llsb-modal__confirm');
      choose.type = 'button';
      choose.setAttribute('data-llbo-choose', bundle.id);
      if (bundle.available) {
        choose.textContent = 'Choose your books';
        choose.addEventListener('click', function () { options.onChoose(bundle); });
      } else {
        choose.textContent = 'Sold out';
        choose.disabled = true;
      }
      // Existing separate-price comparisons describe the classic set. New
      // combinations have different single-book prices, so show their total.
      // The collection's comparison is already guarded by the catalogue (six
      // equal singles, compare-at exactly six times them).
      var priceOffer = bundle.kind !== 'library' && flexibleBundle(bundle, catalogue, storyIds.map(function (id) { return { storyId: id }; }))
        ? Object.assign({}, bundle, { compareAtFormatted: null, percentOff: 0 }) : bundle;
      card.appendChild(foot(priceBlock(priceOffer), choose));
      container.appendChild(card);
      cards.push(card);
    });
    return cards;
  }

  /* ================= slot validation + property builder ================= */
  /* Shared by the bundle page's builder (Task 8): what one slot is called
     in copy and in the property names, whether a set of slots is complete,
     and the storybook-bundle-v2 property list built from them. */

  function itemWord(bundle, region) {
    return bundle.kind === 'colouring' ? colouringWord(region, true) + ' book' : 'Book';
  }

  /* First problem across the slots, or null. slots: [{ index, storyId,
     child: { name, photoFile, ref }, colouring?: { child } }] — the same
     shape the property builder takes, so a page validates exactly what it
     is about to post. In a mixed bundle `colouring.child` is the matching
     colouring book's child; without a ref it is a child of its own. */
  /* Owner, 2026-10-24: the two-story pair may be for one child or two (Book 2
     carries its own name and photo). Their Complete Collection and the
     retired colouring double and Read & Colour stay one child. */
  function requiresSingleChild(bundle) {
    var id = typeof bundle === 'string' ? bundle : bundle && bundle.id;
    return id === 'two-colouring' || id === 'gift-set' || id === COLLECTION_ID;
  }

  function groupWord(bundle) { return bundle.kind === 'library' ? 'collection' : 'bundle'; }

  // New writes share one identity. Legacy cart reads still use the v2 parser.
  function singleChildProblem(bundle, slots, region) {
    if (!requiresSingleChild(bundle)) return null;
    var primary = slots[0] && slots[0].child;
    var expected = bundle.id === 'gift-set' ? 3 : bundle.id === COLLECTION_ID ? COLLECTION_STORY_IDS.length : 2;
    if (!primary || slots.length !== expected || primary.ref) return { index: 1, message: 'Please check the books in your ' + groupWord(bundle) + '.' };
    for (var i = 0; i < slots.length; i += 1) {
      var slot = slots[i], child = slot.child;
      if (slot.index !== i + 1 || !child || child.name !== primary.name || child.photoFile !== primary.photoFile
        || (i > 0 && child.ref !== itemWord(bundle, region) + ' 1')) return { index: 0, message: 'Use the same child’s name and photo for every book in this ' + groupWord(bundle) + '.' };
      if (bundle.id === 'gift-set' && slot.colouring && slot.colouring.child) {
        var colouring = slot.colouring.child;
        if (colouring.name !== primary.name || colouring.photoFile !== primary.photoFile || colouring.ref !== 'Book ' + slot.index)
          return { index: 0, message: 'Use the same child’s name and photo for every book in this collection.' };
      }
    }
    return null;
  }

  /* The collection's six books, in their one order, on reads as well as
     writes: a cart line in any other shape is not this bundle. */
  function fixedOrderProblem(bundle, slots) {
    if (bundle.kind !== 'library') return null;
    var wanted = COLLECTION_STORY_IDS.slice(0, slotCount(bundle));
    for (var i = 0; i < Math.max(slots.length, wanted.length); i += 1) {
      var slot = slots[i];
      if (!slot || slot.index !== i + 1 || slot.storyId !== wanted[i]) return { index: i + 1, message: 'Please check the books in your collection.' };
    }
    return null;
  }

  /* booksOnly: the guided journey's book step checks the choices and names
     alone; photos wait for Add to basket, where buildBundleProperties checks
     them again (owner, 2026-10-19: the photo can wait until Add to basket). */
  function validateSlots(bundle, catalogue, slots, region, legacyRead, booksOnly) {
    // Every bundle line has exactly its own number of books, whoever they are for.
    var count = slotCount(bundle);
    if (typeof count === 'number' && count > 0 && slots.length !== count) return { index: 1, message: 'Please check the books in your ' + groupWord(bundle) + '.' };
    var identityProblem = !legacyRead && singleChildProblem(bundle, slots, region);
    if (identityProblem) return identityProblem;
    var orderProblem = fixedOrderProblem(bundle, slots);
    if (orderProblem) return orderProblem;
    var word = itemWord(bundle, region);
    var nameProblem = ': please enter a name using Latin letters, spaces, apostrophes and hyphens (12 characters or fewer).';
    for (var i = 0; i < slots.length; i += 1) {
      var slot = slots[i];
      var label = word + ' ' + slot.index;
      if (!slot.storyId || !slotItem(bundle, catalogue, slot.storyId)) return { index: slot.index, message: label + ': please choose a book.' };
      if (slots.some(function (other) { return other.index !== slot.index && other.storyId === slot.storyId; })) return { index: slot.index, message: label + ': please choose a different book.' };
      var child = slot.child || {};
      /* A photo ref is a later book reusing Book 1's child: same name and
         photo, or the worker holds the paid line
         (invalid_bundle_photo_reference). A second child posts their own. */
      var first = (slots[0] && slots[0].child) || {};
      if (child.ref && (i === 0 || child.ref !== word + ' 1' || child.name !== first.name || child.photoFile !== first.photoFile)) return { index: slot.index, message: 'Please check the books in your ' + groupWord(bundle) + '.' };
      if (!child.photoFile && !booksOnly) return { index: slot.index, message: label + ': please upload their photo.' };
      if (!validName(child.name)) return { index: slot.index, message: label + nameProblem };
      if (bundle.kind === 'mixed' && !collectionColouring(catalogue, slot, slots)) return { index: slot.index, message: 'Please check the three ' + colouringWord(region) + ' books in this collection.' };
      if (bundle.kind === 'mixed' && slot.colouring && slot.colouring.child && !slot.colouring.child.ref) {
        var cLabel = colouringWord(region, true) + ' book ' + slot.index;
        if (!slot.colouring.child.photoFile && !booksOnly) return { index: slot.index, message: cLabel + ': please upload their photo.' };
        if (!validName(slot.colouring.child.name)) return { index: slot.index, message: cLabel + nameProblem };
      }
    }
    return null;
  }

  /* Builds the storybook-bundle-v2 property list. Entries are [name, value]
     or [name, File, filename]; the host appends its own token / consent /
     Personalisation entries and posts. */
  function buildBundleProperties(bundle, catalogue, slots, region) {
    region = region || 'GB';
    var problem = validateSlots(bundle, catalogue, slots, region);
    if (problem) throw new Error(problem.message);
    var props = [];
    var primaryName = slots[0] && slots[0].child ? slots[0].child.name : '';
    props.push(['Bundle', bundleTitle(bundle, primaryName)]);
    props.push(['_Bundle contract', bundleContract(bundle, catalogue, slots)]);
    props.push(['_Bundle ID', bundle.id]);
    var colouringLabel = colouringWord(region, true) + ' book';

    if (bundle.kind === 'colouring') {
      slots.forEach(function (slot) {
        var book = colouringForStory(catalogue, slot.storyId);
        var child = slot.child;
        var n = slot.index;
        props.push([colouringLabel + ' ' + n, book.title + ' — ' + child.name]);
        props.push(['_Colouring ' + n + ' story ID', book.colouringStoryId || book.storyId]);
        props.push(['_Colouring ' + n + ' child name', child.name]);
        props.push(['_Colouring ' + n + ' product', book.handle]);
        if (child.ref) props.push(['_Colouring ' + n + ' photo ref', child.ref]);
        else props.push(['_Colouring ' + n + ' photo', child.photoFile, child.photoFile.name]);
      });
      return props;
    }

    slots.forEach(function (slot) {
      var story = storyById(catalogue, slot.storyId);
      var child = slot.child;
      var n = slot.index;
      props.push(['Book ' + n, story.title + ' — ' + child.name]);
      props.push(['_Book ' + n + ' story ID', story.storyId]);
      props.push(['_Book ' + n + ' child name', child.name]);
      props.push(['_Book ' + n + ' product', story.handle]);
      if (child.ref) props.push(['_Book ' + n + ' photo ref', child.ref]);
      else props.push(['_Book ' + n + ' photo', child.photoFile, child.photoFile.name]);
    });
    if (bundle.kind === 'mixed') {
      slots.forEach(function (slot) {
        var book = collectionColouring(catalogue, slot, slots);
        /* the colouring book's own child when the slot carries one;
           otherwise it is the story's child, referenced by book number */
        var own = slot.colouring && slot.colouring.child ? slot.colouring.child : null;
        var child = own || slot.child;
        var n = slot.index;
        props.push([colouringLabel + ' ' + n, book.title + ' — ' + child.name]);
        props.push(['_Colouring ' + n + ' story ID', book.colouringStoryId || book.storyId]);
        props.push(['_Colouring ' + n + ' child name', child.name]);
        props.push(['_Colouring ' + n + ' product', book.handle]);
        if (own && !own.ref) props.push(['_Colouring ' + n + ' photo', own.photoFile, own.photoFile.name]);
        else props.push(['_Colouring ' + n + ' photo ref', (own && own.ref) || 'Book ' + n]);
      });
    }
    return props;
  }

  /* Existing cart photos remain Shopify upload URLs. Keep them as strings
     when editing; only a newly selected File needs a multipart filename. */
  function appendProperty(form, entry) {
    var key = 'properties[' + entry[0] + ']';
    if (entry.length === 3 && typeof entry[1] !== 'string') form.append(key, entry[1], entry[2]);
    else form.append(key, entry[1]);
  }

  var photoUploadCache = new WeakMap();
  /* Every cart property that can carry a child's photo. A File under one of
     these keys goes to the private intake and only its signed reference
     enters the cart, so no photo becomes a permanent public Shopify upload. */
  var PHOTO_KEY = /^_(?:Customer photo|Book [1-6] photo)$/;
  var PHOTO_FIELD = /^properties\[(_(?:Customer photo|Book [1-6] photo))\]$/;
  /* Any other property whose name mentions a photo must already be text
     (a "photo ref" pointer or an existing reference). A file left under such
     a key would post to /cart/add.js as a public upload, so preparation
     refuses rather than letting it through. That includes `_Colouring N
     photo`: colouring books were retired on 2026-10-18 and no worker step
     keeps an intake copy for them, so a colouring photo is refused here
     rather than uploaded publicly or left to expire in the intake. */
  var PHOTO_LIKE_KEY = /photo/i;
  var LEFTOVER_PHOTO_COPY = 'We couldn’t save their photo. Please try again.';
  function isFileValue(value) {
    return value !== null && typeof value === 'object' && typeof value !== 'string';
  }
  function privatePhotoReference(value) {
    return typeof value === 'string' && /^ll-photo:v1:[a-f0-9]{32}:[a-f0-9]{64}:[0-9]{10}:[a-f0-9]{64}$/.test(value);
  }
  /* The photo the shopper picked, copied into memory from the bytes the
     photo check already read. Android Chrome can lose access to a picked
     file (a Google Photos item, a changed download) before Add to basket
     streams it to the intake; the upload then fails in the browser without
     reaching us (2026-10-18). A copy cannot go stale. Falls back to the
     original file when the data URL is not base64 or decoding fails. */
  function inMemoryPhoto(file, dataUrl) {
    try {
      var text = String(dataUrl || ''), comma = text.indexOf(',');
      if (!file || comma < 0 || !/;base64$/i.test(text.slice(0, comma)) || typeof File !== 'function') return file;
      var binary = atob(text.slice(comma + 1)), bytes = new Uint8Array(binary.length);
      for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new File([bytes], file.name || 'photo', { type: file.type, lastModified: file.lastModified });
    } catch (error) { return file; }
  }
  /* The intake takes one upload at a time across every shopper and turns a
     second away with 429 "Please try again shortly"
     (preview_api/app.py _PhotoIntakeCapacityMiddleware). A pair for two
     children has two photos, so they go one after the other, and that
     refusal (another shopper's upload in the slot) is tried again twice,
     after a growing, slightly random pause so two waiting shoppers do not
     collide again. */
  var INTAKE_BUSY = 'Please try again shortly';
  var INTAKE_RETRY_MS = 1200;
  var INTAKE_ATTEMPTS = 3;
  function uploadPrivatePhoto(file, fetchImpl, wait, attempt) {
    var form = new FormData();
    form.append('photo', file, file.name);
    form.append('consent', 'adult-authority-v2026-07-21');
    /* The intake is a different origin to the storefront, so a request
       that never produces a readable response — the browser blocking a
       reply that arrived without CORS headers, a dropped connection, a
       timeout mid-upload — rejects with the browser's own TypeError.
       Both callers print error.message straight at the shopper, so that
       surfaced as a bare "Failed to fetch" (Chrome) or "Load failed"
       (Safari) under the Add to basket button. A transport failure is
       ours to explain, not the browser's: it is turned into copy here,
       at the one place that knows the request was the photo upload, so
       every caller inherits it. */
    return fetchImpl('https://preview-api.labelandleaf.com/storybook/customer-photos', { method: 'POST', body: form, credentials: 'omit', cache: 'no-store' }).catch(function () {
      throw new Error('We couldn’t save their photo just now. Please check your connection and try again.');
    }).then(function (response) {
      if (!response.ok) return response.json().catch(function () { return {}; }).then(function (body) {
        if (response.status === 429 && body.detail === INTAKE_BUSY && attempt + 1 < INTAKE_ATTEMPTS) {
          var pause = INTAKE_RETRY_MS * (attempt + 1) + Math.floor(Math.random() * 400);
          return wait(pause).then(function () { return uploadPrivatePhoto(file, fetchImpl, wait, attempt + 1); });
        }
        throw new Error(typeof body.detail === 'string' && body.detail.length < 180 ? body.detail : 'We couldn’t save their photo. Please try again.');
      });
      return response.json().then(function (result) {
        if (!result || !privatePhotoReference(result.reference)) throw new Error('We couldn’t confirm their photo upload. Please try again.');
        return result.reference;
      });
    });
  }
  function preparePhotoEntries(entries, fetchImpl, wait) {
    fetchImpl = fetchImpl || function (url, init) { return fetch(url, init); };
    wait = wait || function (ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); };
    // Each new upload starts once the one before it has finished.
    var queue = Promise.resolve();
    return Promise.all(entries.map(function (entry) {
      if (!PHOTO_KEY.test(entry[0]) || typeof entry[1] === 'string') return entry;
      var file = entry[1];
      var cached = photoUploadCache.get(file);
      if (!cached || cached.created < Date.now() - 86400000) {
        var promise = queue.then(function () { return uploadPrivatePhoto(file, fetchImpl, wait, 0); })
          .catch(function (error) { photoUploadCache.delete(file); throw error; });
        queue = promise;
        cached = { created: Date.now(), promise: promise };
        photoUploadCache.set(file, cached);
      }
      return cached.promise.then(function (reference) { return [entry[0], reference]; });
    })).then(function (prepared) {
      prepared.forEach(function (entry) {
        if (PHOTO_LIKE_KEY.test(entry[0]) && isFileValue(entry[1])) throw new Error(LEFTOVER_PHOTO_COPY);
      });
      return prepared;
    });
  }
  function preparePhotoForm(form, fetchImpl) {
    var entries = [];
    form.forEach(function (value, key) {
      var match = PHOTO_FIELD.exec(key);
      if (match) entries.push([match[1], value]);
    });
    return preparePhotoEntries(entries, fetchImpl).then(function (prepared) {
      prepared.forEach(function (entry) { form.set('properties[' + entry[0] + ']', entry[1]); });
      /* Fail closed: after preparation no photo-named property may still
         hold a file, whatever its exact key. */
      form.forEach(function (value, key) {
        if (/^properties\[/.test(key) && PHOTO_LIKE_KEY.test(key) && isFileValue(value)) throw new Error(LEFTOVER_PHOTO_COPY);
      });
      return form;
    });
  }

  /* The child's photo on the basket's book thumbnail, on this device only
     (owner, 2026-10-19). The cover uploaded with the line stays photo-free,
     because the photo itself only ever goes to the private intake. A copy
     of that cover with the photo in the child's spot stays in this browser,
     keyed by a hash of the line's first book and private photo reference,
     and the basket views swap it in (ll-header-v2.js, same key and store).
     Best effort: it never holds up or fails an add. */
  var BASKET_PHOTO_COVERS = 'll:basket-photo-covers';
  var BASKET_PHOTO_COVERS_MAX = 6;
  var BASKET_PHOTO_COVER_MS = 14 * 24 * 60 * 60 * 1000;
  function basketPhotoCoverSource(properties) {
    var p = properties || {};
    var single = p['_Customer photo'] && p['_Story ID'];
    var story = single ? p['_Story ID'] : p['_Book 1 story ID'];
    var ref = single ? p['_Customer photo'] : (p['_Book 1 photo'] || p['_Book 1 photo ref']);
    return typeof story === 'string' && typeof ref === 'string' && story && privatePhotoReference(ref) ? 'v1|' + story + '|' + ref : '';
  }
  function basketPhotoCoverKey(properties) {
    var source = basketPhotoCoverSource(properties);
    if (!source || !window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') return Promise.resolve('');
    return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(source)).then(function (hash) {
      return Array.prototype.map.call(new Uint8Array(hash), function (b) { return ('0' + b.toString(16)).slice(-2); }).join('').slice(0, 32);
    }, function () { return ''; });
  }
  /* The cover with the photo as a 3:4 tag at the foot of the pose box, as
     the preview's cover tag sits, white-edged; 360px wide is twice the
     basket's largest thumbnail. */
  function photoCoverDataUrl(cover, photo, pose) {
    function load(blob) {
      return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(blob), image = new Image();
        image.onload = function () { URL.revokeObjectURL(url); resolve(image); };
        image.onerror = function () { URL.revokeObjectURL(url); reject(new Error('image')); };
        image.src = url;
      });
    }
    return Promise.all([load(cover), load(photo)]).then(function (images) {
      var art = images[0], face = images[1];
      var width = 360, height = Math.round(width * art.naturalHeight / art.naturalWidth);
      var canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(art, 0, 0, width, height);
      var boxW = pose.w * width / 100, boxH = pose.h * height / 100;
      var tagW = Math.min(boxW, boxH * 3 / 4), tagH = tagW * 4 / 3;
      var x = pose.x * width / 100 + (boxW - tagW) / 2, y = pose.y * height / 100 + boxH - tagH;
      var edge = Math.max(2, Math.round(width * 0.008));
      ctx.save();
      ctx.shadowColor = 'rgba(30, 37, 37, 0.3)'; ctx.shadowBlur = edge * 2; ctx.shadowOffsetY = edge / 2;
      ctx.fillStyle = '#fff'; ctx.fillRect(x - edge, y - edge, tagW + 2 * edge, tagH + 2 * edge);
      ctx.restore();
      // object-fit: cover, focused a little above the middle (the face).
      var scale = Math.max(tagW / face.naturalWidth, tagH / face.naturalHeight);
      var sw = tagW / scale, sh = tagH / scale;
      var sx = (face.naturalWidth - sw) / 2, sy = (face.naturalHeight - sh) * 0.3;
      ctx.drawImage(face, sx, sy, sw, sh, x, y, tagW, tagH);
      return canvas.toDataURL('image/jpeg', 0.82);
    });
  }
  function readBasketPhotoCovers(storage) {
    try {
      var stored = JSON.parse(storage.getItem(BASKET_PHOTO_COVERS) || '{}');
      return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
    } catch (e) { return {}; }
  }
  function writeBasketPhotoCovers(storage, covers) {
    var keys = Object.keys(covers).sort(function (a, b) { return (covers[b].t || 0) - (covers[a].t || 0); });
    var kept = {};
    keys.slice(0, BASKET_PHOTO_COVERS_MAX).forEach(function (key) { kept[key] = covers[key]; });
    for (var tries = 0; tries < 3; tries++) {
      try { storage.setItem(BASKET_PHOTO_COVERS, JSON.stringify(kept)); return; } catch (e) {
        // Out of room: drop the oldest and try again.
        var oldest = Object.keys(kept).sort(function (a, b) { return (kept[a].t || 0) - (kept[b].t || 0); })[0];
        if (!oldest) return;
        delete kept[oldest];
      }
    }
  }
  function rememberBasketPhotoCover(properties, cover, photo, pose, storage) {
    var work = Promise.resolve().then(function () {
      storage = storage || window.localStorage;
      if (!storage || !cover || !photo || !pose || !(pose.w > 0) || !(pose.h > 0)) return;
      return Promise.all([basketPhotoCoverKey(properties), photoCoverDataUrl(cover, photo, pose)]).then(function (made) {
        if (!made[0]) return;
        var covers = readBasketPhotoCovers(storage), now = Date.now();
        Object.keys(covers).forEach(function (key) { if (!covers[key] || now - (covers[key].t || 0) > BASKET_PHOTO_COVER_MS) delete covers[key]; });
        covers[made[0]] = { t: now, src: made[1] };
        writeBasketPhotoCovers(storage, covers);
      });
    }).catch(function () {});
    // Never hold the add up for long.
    return Promise.race([work, new Promise(function (resolve) { setTimeout(resolve, 1500); })]);
  }
  function formProperties(form) {
    var properties = {};
    form.forEach(function (value, key) {
      var match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === 'string') properties[match[1]] = value;
    });
    return properties;
  }

  /* The fragment carries only an opaque line key. Personalisation is read
     from this browser's cart, never from query strings or local storage. */
  function requestedCartEdit(hash) {
    var params = new URLSearchParams(String(hash || '').replace(/^#/, ''));
    return params.has('edit-bundle') ? params.get('edit-bundle') : null;
  }

  function createCartEditor(options) {
    var fetchImpl = options.fetchImpl || function (url, init) { return fetch(url, init); };
    var navigate = options.navigate || function (url) { window.location.href = url; };
    var cartUrl = options.cartUrl || '/cart';
    var phase = 'loading';
    var original = null;
    var token = null;
    var word = basketWord(options.region);
    function status(next, message) {
      phase = next;
      if (options.onStatus) options.onStatus(next, message || '');
    }
    function readCart() {
      return fetchImpl(cartUrl + '.js', { cache: 'no-store' }).then(function (response) {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      }).then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) throw new Error('Cart unavailable');
        return cart;
      });
    }
    function sameProperties(a, b) {
      var keys = Object.keys(a || {});
      return keys.length === Object.keys(b || {}).length && keys.every(function (key) { return a[key] === b[key]; });
    }
    function unchanged(item) {
      return item && item.key === original.key && String(item.variant_id) === String(original.variant_id)
        && item.quantity === original.quantity && sameProperties(item.properties, original.properties);
    }
    function stale() { status('stale', 'This item has changed in your ' + word + '. Please return to your ' + word + ' and open it again.'); }
    function load(key) {
      status('loading', 'Loading your books…');
      if (!/^\d+:[a-f0-9]+$/i.test(key || '')) { stale(); return Promise.resolve(null); }
      return readCart().then(function (cart) {
        var item = cart.items.filter(function (candidate) { return candidate.key === key; })[0];
        if (!item || String(item.variant_id) !== String(options.variantId) || !options.validate(item)) { stale(); return null; }
        original = item;
        status('ready');
        return item;
      }).catch(function () { status('failed', 'We couldn’t load your books. Please return to your ' + word + ' and try again.'); return null; });
    }
    function confirmed(cart) {
      var found = cart.items.filter(function (item) {
        return String(item.variant_id) === String(original.variant_id) && item.properties && item.properties['_Bundle edit token'] === token;
      });
      return found.length === 1 && found[0].quantity === original.quantity;
    }
    function complete(cart) {
      if (!confirmed(cart)) return false;
      status('saved');
      navigate(cartUrl);
      return true;
    }
    function uncertain() {
      /* A lost response is not permission to post again. Read back once;
         otherwise leave the cart intact and ask the shopper to inspect it. */
      return readCart().then(function (cart) {
        if (complete(cart)) return true;
        status('uncertain', 'We couldn’t confirm the update. Please check your ' + word + ' before trying again.');
        return false;
      }).catch(function () {
        status('uncertain', 'We couldn’t confirm the update. Please check your ' + word + ' before trying again.');
        return false;
      });
    }
    function save(entries) {
      if (phase !== 'ready' || !original) return Promise.resolve(false);
      status('saving', 'Saving your books…');
      return readCart().then(function (cart) {
        var item = cart.items.filter(function (candidate) { return candidate.key === original.key; })[0];
        if (!unchanged(item)) { stale(); return false; }
        var form = new FormData();
        form.append('id', original.key);
        form.append('quantity', String(original.quantity));
        /* Properties replace the whole object. Preserve unrelated metadata,
           replacing the complete bundle contract so old photo refs cannot
           survive a switch between shared and individual children. */
        Object.keys(original.properties || {}).forEach(function (key) {
          if (/^(Character|_Personalised artwork|Bundle|_Bundle (contract|ID|edit token)|(?:Book|Colouring book|Coloring book) \d+|_(?:Book|Colouring) \d+ (?:story ID|child name|product|photo|photo ref))$/.test(key)) return;
          if (entries.some(function (entry) { return entry[0] === key; })) return;
          appendProperty(form, [key, original.properties[key]]);
        });
        entries.forEach(function (entry) { appendProperty(form, entry); });
        token = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : 'edit-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
        appendProperty(form, ['_Bundle edit token', token]);
        return fetchImpl(cartUrl + '/change.js', { method: 'POST', body: form }).then(function (response) {
          if (response.ok) return response.json().then(function (body) { return complete(body) || uncertain(); }).catch(uncertain);
          if (response.status >= 400 && response.status < 500) {
            status('ready', 'We couldn’t save those changes. Please check your details and try again.');
            return false;
          }
          return uncertain();
        }).catch(uncertain);
      }).catch(function () {
        status('ready', 'We couldn’t check your ' + word + '. Please try again.');
        return false;
      });
    }
    return { load: load, save: save, phase: function () { return phase; } };
  }

  /* ================= compact selection (phones) ================= */
  /* Below 640px the CSS lays the cards out as one list of rows and hides
     each card's own button; this makes the rows selectable (radio
     semantics) and drives the single footer button from the selected
     row's own button, so the two layouts share one code path per offer.
     The recommended card (or the middle one of three) starts selected. */
  function compactSelect(cardsEl, footEl, options) {
    if (!cardsEl || !footEl) return;
    var button = footEl.querySelector('[data-llsb-modal-go], [data-llcb-modal-go]') || footEl.querySelector('button');
    var cards = Array.prototype.slice.call(cardsEl.children);
    if (!button || cards.length < 2) { footEl.hidden = true; return; }
    /* a bundle landing page links here with ?bundle=<id> — that offer
       starts selected; otherwise the recommended card, else the middle one */
    var wanted = (options && options.preselectId) || '';
    var preferred = cards.filter(function (card) { return wanted && card.getAttribute('data-llbo-bundle') === wanted; })[0]
      || cards.filter(function (card) { return card.classList.contains('llsb-offer--recommended'); })[0]
      || (cards.length === 3 ? cards[1] : cards[0]);
    cardsEl.setAttribute('role', 'radiogroup');
    cardsEl.setAttribute('aria-label', 'Editions');
    function select(card) {
      cards.forEach(function (candidate) {
        var on = candidate === card;
        candidate.classList.toggle('is-selected', on);
        candidate.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      var own = card.querySelector('.llsb-offer__confirm');
      button.textContent = own ? own.textContent : '';
      button.disabled = !own || own.disabled;
    }
    cards.forEach(function (card) {
      card.setAttribute('role', 'radio');
      card.tabIndex = 0;
      card.addEventListener('click', function (event) {
        if (event.target.closest && event.target.closest('.llsb-offer__confirm')) return;
        select(card);
      });
      card.addEventListener('keydown', function (event) {
        if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); select(card); }
      });
    });
    button.onclick = function () {
      var current = cards.filter(function (card) { return card.classList.contains('is-selected'); })[0];
      var own = current && current.querySelector('.llsb-offer__confirm');
      if (own && !own.disabled) own.click();
    };
    select(preferred);
    footEl.hidden = false;
  }

  /* the bundle a landing page asked for, if the URL carries one */
  function requestedBundleId() {
    try {
      var id = new URLSearchParams(window.location.search).get('bundle') || '';
      return /^[a-z-]+$/.test(id) ? id : '';
    } catch (e) { return ''; }
  }

  /* ================= handoff to the bundle page ================= */
  /* A PDP popup card sends the shopper to the bundle product page with the
     child they already personalised. A photo can be 10MB, which rules out
     sessionStorage, so the record goes through IndexedDB and is read once
     (take) within HANDOFF_TTL_MS. Everything fails soft: no IndexedDB, a
     failed write or a stale record simply means the page starts empty. */
  var HANDOFF_TTL_MS = 15 * 60 * 1000;
  var HANDOFF_DB = 'll-books-handoff';
  var HANDOFF_STORE = 'child';
  var HANDOFF_KEY = 'current';

  function memoryHandoffDriver() {
    var map = {};
    return {
      get: function (key) { return Promise.resolve(Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null); },
      set: function (key, value) { map[key] = value; return Promise.resolve(); },
      remove: function (key) { delete map[key]; return Promise.resolve(); }
    };
  }

  function indexedDbHandoffDriver() {
    var idb = null;
    try { idb = window.indexedDB || null; } catch (e) { idb = null; }
    if (!idb) return null;
    function open() {
      return new Promise(function (resolve, reject) {
        var request = idb.open(HANDOFF_DB, 1);
        request.onupgradeneeded = function () { request.result.createObjectStore(HANDOFF_STORE); };
        request.onsuccess = function () { resolve(request.result); };
        request.onerror = function () { reject(request.error); };
        request.onblocked = function () { reject(new Error('blocked')); };
      });
    }
    function run(mode, work) {
      return open().then(function (db) {
        return new Promise(function (resolve, reject) {
          var tx = db.transaction(HANDOFF_STORE, mode);
          var request = work(tx.objectStore(HANDOFF_STORE));
          tx.oncomplete = function () { db.close(); resolve(request ? request.result : undefined); };
          tx.onerror = function () { db.close(); reject(tx.error); };
          tx.onabort = function () { db.close(); reject(tx.error || new Error('aborted')); };
        });
      });
    }
    /* Some WebKit contexts reject File/Blob values in IndexedDB while
       accepting ArrayBuffers. Read the bytes before opening a transaction;
       waiting for FileReader inside it would let the transaction expire. */
    function encodePhoto(record) {
      if (!record || !record.photo || !(record.photo instanceof Blob)) return Promise.resolve(record);
      return new Promise(function (resolve, reject) {
        var photo = record.photo;
        var reader = new FileReader();
        reader.onerror = function () { reject(reader.error || new Error('Photo transfer could not be prepared.')); };
        reader.onload = function () {
          var encoded = {};
          Object.keys(record).forEach(function (key) { encoded[key] = record[key]; });
          // An older reader cannot decode this shape; leave its photo null
          // so it asks for an upload instead of treating bytes as a File.
          encoded.photo = null;
          encoded.photoTransfer = {
            format: 'll-photo-bytes-v1', bytes: reader.result,
            name: photo.name || 'child.png', type: photo.type,
            lastModified: photo.lastModified
          };
          resolve(encoded);
        };
        reader.readAsArrayBuffer(photo);
      });
    }
    function decodePhoto(record) {
      var photo = record && record.photoTransfer;
      // Records written by an earlier page version already contain a File.
      if (photo && photo.format === 'll-photo-bytes-v1') {
        if (!(photo.bytes instanceof ArrayBuffer)) throw new Error('Invalid photo transfer.');
        record.photo = new File([photo.bytes], photo.name, { type: photo.type, lastModified: photo.lastModified });
        delete record.photoTransfer;
      }
      return record || null;
    }
    return {
      get: function (key) { return run('readonly', function (store) { return store.get(key); }).then(decodePhoto); },
      set: function (key, value) { return encodePhoto(value).then(function (encoded) { return run('readwrite', function (store) { return store.put(encoded, key); }); }); },
      remove: function (key) { return run('readwrite', function (store) { return store.delete(key); }); }
    };
  }

  function createChildSessionStore(driver, session, clock) {
    var key = 'shopping-child', ttl = 60 * 60 * 1000, queue = Promise.resolve();
    clock = clock || Date.now;
    function run(work) {
      queue = queue.catch(function () {}).then(function () { return driver && session ? work() : null; });
      return queue.catch(function () { return null; });
    }
    return {
      save: function (child) {
        var record = child && child.ready && !child.checking && child.consent === true && validName(child.name) && child.photoFile
          ? { v: 1, session: session, at: clock(), name: child.name, consent: true, photo: child.photoFile } : null;
        return run(function () { return record ? driver.set(key, record) : driver.remove(key); });
      },
      load: function () { return run(function () { return driver.get(key).then(function (record) {
        if (record && record.v === 1 && record.session === session && record.consent === true && validName(record.name)
            && record.photo && /^image\/(jpeg|png|webp)$/.test(record.photo.type) && record.photo.size > 0 && record.photo.size <= 10 * 1024 * 1024
            && clock() >= record.at && clock() - record.at < ttl) return record;
        return driver.remove(key).then(function () { return null; });
      }); }); },
      clear: function () { return run(function () { return driver.remove(key); }); }
    };
  }

  function bindChildSession(options) {
    var session = '', revision = 0, loaded = false, previous = null;
    try {
      session = window.sessionStorage.getItem('ll:shopping-session') || window.crypto.randomUUID();
      window.sessionStorage.setItem('ll:shopping-session', session);
    } catch (_) { return { update: function () {} }; }
    var store = createChildSessionStore(indexedDbHandoffDriver(), session);
    function update() {
      if (!loaded) return;
      var child = options.read();
      var signature = [child.name, child.photoFile, child.ready, child.checking, child.consent];
      if (previous && signature.every(function (value, i) { return value === previous[i]; })) return;
      previous = signature;
      store.save(child);
    }
    options.inputs.forEach(function (input) {
      if (!input) return;
      ['input', 'change'].forEach(function (event) { input.addEventListener(event, function () {
        revision++; loaded = true; window.setTimeout(update, 0);
      }); });
    });
    store.load().then(function (record) {
      if (!record || revision || options.read().photoFile || options.restoreAllowed === false) return;
      var reader = new FileReader();
      reader.onload = function () {
        var probe = new Image();
        probe.onload = function () {
          if (revision || options.read().photoFile || probe.naturalWidth < 500 || probe.naturalHeight < 500) return;
          loaded = true;
          options.restore(record, String(reader.result), probe.naturalWidth, probe.naturalHeight);
          update();
        };
        probe.src = String(reader.result);
      };
      reader.readAsDataURL(record.photo);
    }).catch(function () {}).finally(function () { loaded = true; });
    return { update: update };
  }

  function createHandoffStore(driver) {
    return {
      save: function (record) { return driver ? driver.set(HANDOFF_KEY, record) : Promise.reject(new Error('no handoff storage')); },
      take: function () {
        if (!driver) return Promise.resolve(null);
        return driver.get(HANDOFF_KEY).then(function (record) {
          return driver.remove(HANDOFF_KEY).catch(function () {}).then(function () { return record || null; });
        }).catch(function () { return null; });
      },
      clear: function () { return driver ? driver.remove(HANDOFF_KEY).catch(function () {}) : Promise.resolve(); }
    };
  }

  function handoffRecord(options) {
    var child = options.child || {};
    return {
      v: 1,
      at: options.now != null ? options.now : Date.now(),
      bundleId: options.bundle.id,
      fromStoryId: options.storyId || '',
      name: child.name || '',
      consent: true,
      photo: child.photoFile || null
    };
  }

  function validHandoff(record, bundleId, now) {
    if (!record || record.v !== 1 || record.bundleId !== bundleId || !record.photo) return false;
    var at = Number(record.at);
    var t = now != null ? now : Date.now();
    return isFinite(at) && t - at >= 0 && t - at < HANDOFF_TTL_MS;
  }

  function bundlePageUrl(bundle, storyId) {
    return bundle.url + '?bundle=' + encodeURIComponent(bundle.id) + '&from=' + encodeURIComponent(storyId || '');
  }

  function handoffToBundlePage(bundle, options) {
    var store = options.store || createHandoffStore(indexedDbHandoffDriver());
    var navigate = options.navigate || function (url) { window.location.href = url; };
    var url = bundlePageUrl(bundle, options.storyId);
    return store.save(handoffRecord({ bundle: bundle, storyId: options.storyId, child: options.child }))
      .catch(function () {})
      .then(function () { navigate(url); });
  }

  function requestedFromStoryId() {
    try {
      var id = new URLSearchParams(window.location.search).get('from') || '';
      return /^[a-z_]+$/.test(id) ? id : '';
    } catch (e) { return ''; }
  }

  /* ================= shared /cart/add.js poster ================= */
  /* Token + reconcile + redirect, lifted from the colouring PDP so the
     colouring page and the bundle page share one implementation. The story
     page keeps its own flow (it composites a cover proof first). */
  function createCartAdder(options) {
    var fetchImpl = options.fetchImpl || function (url, init) { return fetch(url, init); };
    var navigate = options.navigate || function (url) { window.location.href = url; };
    var wait = options.setTimeout || function (fn, ms) { window.setTimeout(fn, ms); };
    var storage = options.storage || null;
    var reconcileDelays = [250, 750, 1500, 3000];
    var word = basketWord(options.region);
    var Word = word.charAt(0).toUpperCase() + word.slice(1);
    var adding = false;
    var pendingAdd = readPending();

    function setStatus(message, kind) {
      var node = options.statusNode;
      if (!node) return;
      node.hidden = !message;
      node.className = 'llsb__upload-status' + (kind ? ' is-' + kind : '');
      node.textContent = message || '';
    }
    function createToken() {
      try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID(); } catch (e) { /* fallback */ }
      return (options.tokenPrefix || 'add') + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    }
    function readPending() {
      if (!storage) return null;
      try { var v = JSON.parse(storage.getItem(options.storageKey) || 'null'); return v && typeof v.token === 'string' ? v : null; } catch (e) { return null; }
    }
    function remember(token, status) {
      if (!storage) return false;
      var record = { token: token, status: status, createdAt: Date.now() };
      try { storage.setItem(options.storageKey, JSON.stringify(record)); pendingAdd = record; return true; } catch (e) { return false; }
    }
    function clearPending() { try { if (storage) storage.removeItem(options.storageKey); } catch (e) { /* no-op */ } pendingAdd = null; }
    function setControls(disabled, label) {
      (options.controls ? options.controls() : []).forEach(function (button) { button.disabled = disabled; if (label) button.textContent = label; });
    }
    function reset() {
      adding = false;
      var ok = typeof options.available === 'function' ? options.available() : (options.available !== false);
      setControls(!ok, options.idleLabel);
    }
    function cartContainsToken(token) {
      return fetchImpl('/cart.js', { cache: 'no-store' }).then(function (response) {
        if (!response.ok) throw new Error(Word + ' check failed.');
        return response.json();
      }).then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) throw new Error(Word + ' check returned invalid data.');
        return cart.items.some(function (item) { return item && item.properties && item.properties[options.tokenProperty] === token; });
      });
    }
    function pollForToken(token, index) {
      return cartContainsToken(token).then(function (found) {
        if (found || index >= reconcileDelays.length) return found;
        return new Promise(function (resolve) { wait(resolve, reconcileDelays[index]); }).then(function () { return pollForToken(token, index + 1); });
      });
    }
    function showUnknown(token) {
      adding = true;
      remember(token, 'unknown');
      setControls(true, 'Check ' + word);
      var node = options.statusNode;
      if (!node) return;
      setStatus('', 'bad');
      node.hidden = false;
      node.textContent = 'We couldn’t verify whether this reached your ' + word + '. ';
      if (typeof document === 'undefined') return;
      var link = document.createElement('a');
      link.href = options.cartUrl || '/cart';
      link.textContent = 'Open your ' + word;
      node.appendChild(link);
      node.appendChild(document.createTextNode(' · '));
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'llsb__status-action';
      retry.textContent = 'Not there? Reset form';
      retry.addEventListener('click', function () {
        retry.disabled = true;
        cartContainsToken(token).then(function (found) {
          clearPending();
          if (found) { navigate(options.cartUrl || '/cart'); return; }
          reset();
          setStatus(Word + ' checked — you can try again.', 'ok');
        }).catch(function () { showUnknown(token); });
      });
      node.appendChild(retry);
    }
    function reconcile() {
      if (!pendingAdd) return Promise.resolve(false);
      var token = pendingAdd.token;
      adding = true;
      setControls(true, 'Checking ' + word + '…');
      setStatus('Checking whether your personalised book reached the ' + word + '…', '');
      return pollForToken(token, 0).then(function (found) {
        if (found) { clearPending(); navigate(options.cartUrl || '/cart'); return true; }
        showUnknown(token);
        return false;
      }).catch(function () { showUnknown(token); return false; });
    }
    function definiteFailure(message) {
      clearPending();
      reset();
      setStatus(message || ('Sorry — we couldn’t add that to your ' + word + '. Please try again.'), 'bad');
      /* the journey retires it on the next step (clearAddFailure) */
      var node = options.statusNode;
      if (node && node.setAttribute) node.setAttribute('data-llsb-add-failure', node.textContent);
    }
    function add(form, hooks) {
      hooks = hooks || {};
      if (pendingAdd) return reconcile();
      if (adding) return Promise.resolve(false);
      adding = true;
      setStatus('', '');
      setControls(true, 'Adding…');
      var token = createToken();
      if (!remember(token, 'posting')) {
        definiteFailure('We couldn’t safely start this ' + word + ' update. Please refresh and try again.');
        return Promise.resolve(false);
      }
      form.append('properties[' + options.tokenProperty + ']', token);
      return fetchImpl('/cart/add.js', { method: 'POST', body: form }).then(function (response) {
        if (response.ok) {
          clearPending();
          if (hooks.onAdded) { try { hooks.onAdded(); } catch (e) { /* measurement never blocks the redirect */ } }
          navigate(options.cartUrl || '/cart');
          return true;
        }
        if (response.status >= 400 && response.status < 500) {
          return response.json().catch(function () { return null; }).then(function (body) {
            var message = body && (body.description || body.message);
            definiteFailure(message);
            if (hooks.onDefiniteFailure) hooks.onDefiniteFailure(message);
            return false;
          });
        }
        return reconcile();
      }).catch(function () { return reconcile(); });
    }
    return { add: add, reconcile: reconcile, pending: function () { return pendingAdd; }, reset: reset, createToken: createToken, remember: remember, clearPending: clearPending, setControls: setControls, isAdding: function () { return adding; } };
  }

  /* ================= consent banner clearance ================= */
  /* Shopify's privacy banner (#shopify-pc__banner) can sit over a page's
     fixed buy bar (the colouring PDP and the bundle page both reparent
     `.llcb__ctabar` to document.body); this measures the banner's height
     whenever it overlaps the bottom of the viewport and publishes it as
     --llcb-consent-clearance on the document root so `bottom:
     var(--llcb-consent-clearance, 0px)` on the bar keeps it clear. rootEl
     just gates the poll on there being a bar to protect (mirrors each
     page's own "if (!ctabar) return"); the custom property itself always
     lands on document.documentElement, exactly as before this was shared,
     so it cascades to the bar wherever it currently lives in the DOM. */
  function trackConsentClearance(rootEl) {
    if (!rootEl) return;
    var appliedClearance = -1;
    function measure() {
      var banner = document.getElementById('shopify-pc__banner');
      var clearance = 0;
      if (banner) {
        var style = window.getComputedStyle(banner);
        var rect = banner.getBoundingClientRect();
        var visible = style.display !== 'none'
          && style.visibility !== 'hidden'
          && parseFloat(style.opacity || '1') > 0.1
          && rect.height > 0;
        if (visible && rect.bottom > window.innerHeight - 8 && rect.top < window.innerHeight) {
          clearance = Math.ceil(window.innerHeight - rect.top);
        }
      }
      if (clearance !== appliedClearance) {
        appliedClearance = clearance;
        document.documentElement.style.setProperty('--llcb-consent-clearance', clearance + 'px');
      }
    }
    measure();
    window.setInterval(measure, 250);
  }

  /* ================= measurement ================= */
  /* Shopify does not load the Customer Privacy API on this storefront by
     itself (checked live 2026-09-05: only Shopify.loadFeatures exists), so
     module initialisation asks for it before a bundle shopper can add to
     cart. Events queue during loading; true can mean queued, not published.
     A failed load can retry on the next interaction. Nothing on this path
     may delay or block buying. */
  var CONSENT_FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];
  var consentWaiters = null; /* null = not requested; an array = loading */
  function withConsentApi(callback) {
    var shopify = window.Shopify;
    if (!shopify) return false;
    if (shopify.customerPrivacy) return callback(shopify.customerPrivacy) === true;
    if (typeof shopify.loadFeatures !== 'function') return false;
    if (consentWaiters) { consentWaiters.push(callback); return true; }
    consentWaiters = [callback];
    try {
      shopify.loadFeatures(CONSENT_FEATURE, function (error) {
        var waiters = consentWaiters || [];
        consentWaiters = null;
        var privacy = !error && window.Shopify && window.Shopify.customerPrivacy;
        if (!privacy) return;
        waiters.forEach(function (fn) { try { fn(privacy); } catch (e) { /* analytics never blocks buying */ } });
      });
    } catch (e) { consentWaiters = null; return false; }
    return true;
  }

  /* Funnel: viewed (a summary link is half visible) -> selected (an offer is
     chosen) -> completed (valid details at the editor CTA) -> confirmed (the
     /cart/add.js response was OK). offerId is the offer the event is about. */
  function trackOffer(action, context) {
    var events = { viewed: 'll:book_offer_viewed', selected: 'll:book_offer_selected', completed: 'll:book_personalisation_completed', confirmed: 'll:book_personalisation_confirmed' };
    if (!events[action] || !context) return false;
    if (['GB', 'US'].indexOf(context.region) < 0 || ['storybook', 'colouring', 'bundle'].indexOf(context.surface) < 0) return false;
    if (['single', 'two-stories', 'two-colouring', 'gift-set', COLLECTION_ID].indexOf(context.offerId) < 0) return false;
    if (['summary', 'popup', 'builder', 'selector', 'inline', 'pdp'].indexOf(context.placement) < 0) return false;
    var payload = { region: context.region, surface: context.surface, offerId: context.offerId, placement: context.placement };
    try {
      return withConsentApi(function (privacy) {
        var shopify = window.Shopify;
        if (!privacy.analyticsProcessingAllowed() || !shopify.analytics || typeof shopify.analytics.publish !== 'function') return false;
        var result = shopify.analytics.publish(events[action], payload);
        if (result && typeof result.catch === 'function') result.catch(function () {});
        return true;
      });
    } catch (e) { return false; }
  }

  /* ================= offer selector ================= */
  /* Three identical option cards (snippet ll-book-offer-summary.liquid).
     A page that builds the choice in place passes onSelect and gets the
     radio bookkeeping done for it. A page that still sends the shopper to
     the bundle product page passes nothing: an anchor option navigates
     natively, a button option through `navigate`; either way a bundle
     option goes through the handoff record when the child is ready, so
     the photo and name travel with them. Returns { select, selected }. */
  function bindOfferSelector(root, options) {
    var summary = root.querySelector('[data-llbo-summary]');
    var nodes = summary ? Array.prototype.slice.call(summary.querySelectorAll('[data-llbo-option]')) : [];
    var radios = nodes.filter(function (node) { return node.getAttribute('role') === 'radio'; });
    var current = '';
    if (options.catalogue && options.storyId) nodes.forEach(function (node) {
      var bundle = (options.bundles || []).filter(function (entry) { return entry.id === node.getAttribute('data-llbo-option'); })[0];
      var back = node.querySelector('[data-llbo-default-second]');
      if (!bundle || !back) return;
      var ids = defaultStoryIds(bundle, options.catalogue, options.storyId);
      var second = storyById(options.catalogue, ids[1]);
      if (second && second.image) back.src = second.image;
      var thirdNode = node.querySelector('[data-llbo-default-third]');
      var third = thirdNode && storyById(options.catalogue, ids[2]);
      if (third && third.image) thirdNode.src = third.image;
    });
    /* A radio group is one tab stop: the checked option, or the first one
       that can be chosen. Arrow keys, Home and End move and select. */
    function syncTabStops() {
      var enabled = radios.filter(function (node) { return !node.disabled; });
      var stop = enabled.filter(function (node) { return node.getAttribute('aria-checked') === 'true'; })[0] || enabled[0];
      radios.forEach(function (node) { node.tabIndex = node === stop ? 0 : -1; });
    }
    function mark(optionId) {
      current = optionId;
      radios.forEach(function (node) {
        node.setAttribute('aria-checked', node.getAttribute('data-llbo-option') === optionId ? 'true' : 'false');
      });
      syncTabStops();
    }
    nodes.forEach(function (node) {
      if (node.getAttribute('aria-checked') === 'true' || node.getAttribute('aria-current') === 'true') current = node.getAttribute('data-llbo-option') || '';
    });
    radios.forEach(function (node) {
      if (!node.addEventListener) return;
      node.addEventListener('keydown', function (event) {
        if (event.altKey || event.ctrlKey || event.metaKey) return; /* browser Back/Forward, not a choice */
        var enabled = radios.filter(function (radio) { return !radio.disabled; }), index = enabled.indexOf(node), target = null;
        if (index === -1) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') target = enabled[(index + 1) % enabled.length];
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') target = enabled[(index - 1 + enabled.length) % enabled.length];
        else if (event.key === 'Home') target = enabled[0];
        else if (event.key === 'End') target = enabled[enabled.length - 1];
        if (!target) return;
        event.preventDefault();
        target.focus();
        if (target !== node) target.click();
      });
    });
    if (radios.length) syncTabStops();
    var placement = options.placement || 'selector';
    nodes.forEach(function (node) {
      var optionId = node.getAttribute('data-llbo-option') || '';
      var context = { region: options.region, surface: options.surface, offerId: optionId, placement: placement };
      node.addEventListener('click', function (event) {
        event = event || {};
        if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (node.getAttribute('aria-current') === 'true') return;
        trackOffer('selected', context);
        if (options.onSelect) { mark(optionId); options.onSelect(optionId, node); return; }
        var url = node.getAttribute('data-llbo-url');
        if (!url) return;
        var isLink = Boolean(node.getAttribute('href'));
        var navigate = options.navigate || function (target) { window.location.href = target; };
        var bundle = (options.bundles || []).filter(function (offer) { return offer.id === optionId && offer.available; })[0];
        var ready = typeof options.isReady === 'function' && options.isReady();
        if (bundle && ready) {
          if (event.preventDefault) event.preventDefault();
          var store = options.store || createHandoffStore(indexedDbHandoffDriver());
          var child = options.child ? options.child() : { name: '', photoFile: null };
          return store.save(handoffRecord({ bundle: bundle, storyId: options.storyId || '', child: child }))
            .catch(function () {})
            .then(function () { navigate(url); });
        }
        if (!isLink) navigate(url);
      });
      if (typeof window.IntersectionObserver === 'function') {
        var visible = false;
        var reported = false;
        var recordView = function () {
          if (!visible || reported) return;
          withConsentApi(function () {
            // Re-check visibility when the API arrives. Queuing a callback
            // is not a view, and a pre-consent view must not be replayed.
            if (!visible || reported || !trackOffer('viewed', context)) return false;
            reported = true;
            observer.disconnect();
            if (typeof document !== 'undefined' && document.removeEventListener) document.removeEventListener('visitorConsentCollected', recordView);
            return true;
          });
        };
        var observer = new window.IntersectionObserver(function (entries) {
          var latest = entries[entries.length - 1];
          if (latest) visible = latest.isIntersecting && latest.intersectionRatio >= 0.5;
          recordView();
        }, { threshold: 0.5 });
        observer.observe(node);
        if (typeof document !== 'undefined' && document.addEventListener) document.addEventListener('visitorConsentCollected', recordView);
      }
    });
    return { select: mark, selected: function () { return current; } };
  }

  window.LLBooksOffers = {
    CONTRACT: CONTRACT,
    FLEXIBLE_CONTRACT: FLEXIBLE_CONTRACT,
    fixedSlots: fixedSlots,
    hasPrintableBonus: hasPrintableBonus,
    bundleContract: bundleContract,
    collectionColouring: collectionColouring,
    trackOffer: trackOffer,
    bindOfferSelector: bindOfferSelector,
    bindSummary: bindOfferSelector,
    trackConsentClearance: trackConsentClearance,
    requestedBundleId: requestedBundleId,
    HANDOFF_TTL_MS: HANDOFF_TTL_MS,
    memoryHandoffDriver: memoryHandoffDriver,
    indexedDbHandoffDriver: indexedDbHandoffDriver,
    createHandoffStore: createHandoffStore,
    createChildSessionStore: createChildSessionStore,
    bindChildSession: bindChildSession,
    handoffRecord: handoffRecord,
    validHandoff: validHandoff,
    bundlePageUrl: bundlePageUrl,
    handoffToBundlePage: handoffToBundlePage,
    requestedFromStoryId: requestedFromStoryId,
    renderBundleCards: renderBundleCards,
    defaultStoryIds: defaultStoryIds,
    slotCatalogue: slotCatalogue,
    bundleTitle: bundleTitle,
    priceBlock: priceBlock,
    foot: foot,
    compactSelect: compactSelect,
    possessive: possessive,
    validName: validName,
    basketWord: basketWord,
    itemWord: itemWord,
    validateSlots: validateSlots,
    requiresSingleChild: requiresSingleChild,
    buildBundleProperties: buildBundleProperties,
    appendProperty: appendProperty,
    privatePhotoReference: privatePhotoReference,
    inMemoryPhoto: inMemoryPhoto,
    preparePhotoEntries: preparePhotoEntries,
    preparePhotoForm: preparePhotoForm,
    basketPhotoCoverKey: basketPhotoCoverKey,
    rememberBasketPhotoCover: rememberBasketPhotoCover,
    formProperties: formProperties,
    requestedCartEdit: requestedCartEdit,
    createCartEditor: createCartEditor,
    createCartAdder: createCartAdder
  };
  withConsentApi(function () { return false; });
}());
